import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import type { ReactNode } from "react";

import { api, refreshSession, unwrap } from "@/api/client";
import type { User } from "@/api/types";

import { session } from "./session";
import type { SessionState } from "./session";

type Auth = SessionState & {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Show the signed-in person's new details after they edit themselves. */
  updateUser: (user: User) => void;
};

const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(session.subscribe, session.getState);
  const queryClient = useQueryClient();

  // On load, a stored refresh token is exchanged for a live session.
  useEffect(() => {
    if (session.getState().status !== "loading") return;
    if (!session.getRefreshToken()) {
      session.clear();
      return;
    }
    void refreshSession().then((ok) => {
      // Not ok with the token still present means we are offline: show sign-in rather than spin.
      if (!ok && session.getState().status === "loading") session.clear();
    });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const next = await unwrap(api.POST("/v1/auth/login", { body: { email, password, client: "web" } }));
    session.set(next);
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = session.getRefreshToken();
    session.clear();
    queryClient.clear(); // nothing of the previous person may remain in the cache
    if (refreshToken) {
      await api.POST("/v1/auth/logout", { body: { refresh_token: refreshToken } }).catch(() => undefined);
    }
  }, [queryClient]);

  const value = useMemo(() => ({ ...state, signIn, signOut, updateUser: session.updateUser }), [state, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("useAuth must be used inside <AuthProvider>");
  return auth;
}

/** The signed-in person. Only for components rendered behind <RequireAuth>. */
export function useUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error("useUser must be used behind <RequireAuth>");
  return user;
}
